/**
 * Central site configuration — update contact details and URLs here only.
 */
import { LEGAL } from './legal';
import { brandFaviconUrl, brandLogoUrl } from '../constants/images';

export { LEGAL };

export const SITE_CONFIG = {
  name: 'Peppar',
  legalName: LEGAL.entityName,
  tagline: 'Restaurant POS, digital menus & operations — all in one platform',
  contact: {
    email: 'contact@peppar.in',
    /** E.164 without + — used for WhatsApp API links */
    phone: '917337876270',
    phoneDisplay: '+91 733-7876270',
    phoneTel: '+917337876270',
  },
  urls: {
    site: 'https://www.peppar.in',
    partners: 'https://partners.peppar.in',
    partnersSignIn: 'https://partners.peppar.in/signin',
  },
  social: {
    instagram: 'https://www.instagram.com/peppar.in/',
    youtube: 'https://www.youtube.com/@peppar_in',
    facebook: 'https://www.facebook.com/profile.php?id=61590719014081',
    x: 'https://x.com/peppar_in',
    xHandle: '@peppar_in',
  },
  get logo() {
    return {
      url: brandLogoUrl(),
      favicon: brandFaviconUrl(),
    };
  },
  seo: {
    defaultTitle: 'Peppar – Restaurant POS, Digital Menu & Operations Platform',
    defaultDescription:
      'Peppar helps restaurants run billing, kitchen display, digital QR menus, table ordering, inventory, reservations, and customer messaging from one cloud platform. Request a free demo.',
    get ogImage() {
      return brandLogoUrl();
    },
    keywords:
      'Peppar, restaurant POS India, digital menu, QR ordering, kitchen display system, KDS, digital signage, TV menu board, KOT printing, thermal printer POS, ESC/POS, restaurant management software, table reservation, group ordering, Peppy AI, inventory management, GST billing, cloud kitchen, multi-outlet restaurant, Petpooja alternative',
    llmsTxtUrl: 'https://www.peppar.in/llms.txt',
    llmsFullUrl: 'https://www.peppar.in/llms-full.txt',
    aiDocsBaseUrl: 'https://www.peppar.in/ai',
  },
} as const;

export type PlatformSocialLinkKey = 'instagram' | 'youtube' | 'facebook' | 'x';

export interface PlatformSocialLink {
  key: PlatformSocialLinkKey;
  label: string;
  url: string;
  icon: string;
  className: string;
}

export const PLATFORM_SOCIAL_LINKS: PlatformSocialLink[] = [
  {
    key: 'instagram',
    label: 'Instagram',
    url: SITE_CONFIG.social.instagram,
    icon: 'instagram',
    className: 'fp-social-btn--instagram',
  },
  {
    key: 'x',
    label: 'X (Twitter)',
    url: SITE_CONFIG.social.x,
    icon: 'twitter',
    className: 'fp-social-btn--twitter',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    url: SITE_CONFIG.social.facebook,
    icon: 'facebook',
    className: 'fp-social-btn--facebook',
  },
  {
    key: 'youtube',
    label: 'YouTube',
    url: SITE_CONFIG.social.youtube,
    icon: 'youtube-play',
    className: '',
  },
];

export function getPlatformSocialSameAs(): string[] {
  return PLATFORM_SOCIAL_LINKS.map((link) => link.url);
}

/** Production-safe origin for meta tags and share URLs (never localhost in prod). */
export function getSiteOrigin(): string {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return SITE_CONFIG.urls.site;
}

export function getWhatsAppUrl(message?: string): string {
  const text = encodeURIComponent(
    message || 'Hi, I would like to know more about Peppar for my restaurant.'
  );
  return `https://wa.me/${SITE_CONFIG.contact.phone}?text=${text}`;
}

export function getMailtoUrl(subject?: string): string {
  const q = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  return `mailto:${SITE_CONFIG.contact.email}${q}`;
}

/** Partner signup / free trial — always lands on partners sign-in. */
export const PLAY_STORE_APPS = [
  {
    id: 'merchant',
    name: 'Peppar Merchant',
    description: 'Billing, orders & kitchen for restaurant staff',
    href: 'https://play.google.com/store/apps/details?id=in.peppar.merchant',
  },
  {
    id: 'rider',
    name: 'Peppar Rider',
    description: 'Deliveries for Peppar rider partners',
    href: 'https://play.google.com/store/apps/details?id=in.peppar.rider',
  },
] as const;

export function getPartnersSignInUrl(): string {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
      return `${window.location.protocol}//partners.localhost${port}/signin`;
    }
    const parts = hostname.split('.');
    if (parts.length > 2) {
      parts[0] = 'partners';
      return `${window.location.protocol}//${parts.join('.')}${port}/signin`;
    }
  }
  return SITE_CONFIG.urls.partnersSignIn;
}
